import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, toDisplayString as _toDisplayString, Fragment as _Fragment, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, unref as _unref, withKeys as _withKeys, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock, normalizeClass as _normalizeClass, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = ["title", "aria-expanded"]
const _hoisted_2 = { class: "agm-head" }
const _hoisted_3 = ["href"]
const _hoisted_4 = ["aria-expanded", "disabled"]
const _hoisted_5 = {
  key: 2,
  class: "agm-nested"
}
const _hoisted_6 = { class: "agm-nested-title" }
const _hoisted_7 = ["disabled", "onClick"]
const _hoisted_8 = ["aria-expanded", "disabled"]
const _hoisted_9 = {
  key: 0,
  class: "agm-nested"
}
const _hoisted_10 = ["disabled", "onClick"]
const _hoisted_11 = ["disabled"]
const _hoisted_12 = {
  key: 4,
  class: "agm-note"
}
const _hoisted_13 = ["disabled"]
const _hoisted_14 = ["aria-expanded", "disabled"]
const _hoisted_15 = {
  key: 6,
  class: "agm-nested"
}
const _hoisted_16 = ["disabled", "onClick"]
const _hoisted_17 = ["disabled"]
const _hoisted_18 = {
  key: 2,
  class: "agm-problem"
}

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import AgendorPanel from '@/components/agendor/AgendorPanel.vue'
import { usePermission } from '@/composables/usePermission'
import { loadAgendor, requestAgendorLink, useAgendor } from '@/composables/useAgendor'
import { useAgendorPanel, type AgendorPanelFocus } from '@/composables/useAgendorPanel'
import { assignConversation } from '@/services/wootdashConversations'
import { findAgendorContact, moveAgendorDeal, setAgendorDealStatus, setAgendorOwner, type AgendorDeal, type AgendorStage } from '@/services/wootdashAgendor'


export default /*@__PURE__*/_defineComponent({
  __name: 'AgendorMenu',
  props: {
    contactId: {},
    conversationId: { default: null },
    conversationAgentId: { default: null },
    contactName: { default: null },
    variant: { default: 'light' }
  },
  setup(__props: any) {

// Botão "Agendor ▾" com submenus: Mover etapa ▸ (negócio → etapa), Marcar ganho ▸, Passar lead para ▸. O resto abre o
// painel lateral (AgendorPanel) já na seção certa. Os dados vêm do painel (lido quando o menu abre).
const props = __props

const store = useStore()
const { can } = usePermission()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')
const { access, links } = useAgendor()
const state = useAgendorPanel(() => token.value, () => props.contactId)

const canMove = computed(() => can('agendor.mover_etapa'))
const canWinLose = computed(() => can('agendor.ganho_perda'))
const link = computed(() => (props.contactId ? links.get(props.contactId) ?? null : null))
const visible = computed(() => !!access.value?.allowed && !!props.contactId)
const panel = computed(() => state.panel.value)
const busy = computed(() => state.busy.value)
const ongoing = computed(() => (panel.value?.deals ?? []).filter(d => d.status === 'ongoing' && d.funnelId))
const ownerIsMe = computed(() => !!panel.value?.myUserId && panel.value.users.find(u => u.id === panel.value!.myUserId)?.name === link.value?.ownerName)
const passTargets = computed(() => (panel.value?.users ?? []).filter(u => u.name !== link.value?.ownerName))
const title = computed(() => link.value
  ? ['Agendor', link.value.personName, link.value.organizationName, link.value.ownerName ? `responsável ${link.value.ownerName}` : null].filter(Boolean).join(' · ')
  : 'Agendor: contato não vinculado')

const open = ref(false)
const sub = ref<'move' | 'won' | 'pass' | null>(null)
const panelOpen = ref(false)
const focus = ref<AgendorPanelFocus>(null)
const btn = ref<HTMLElement | null>(null)
const menu = ref<HTMLElement | null>(null)
const menuStyle = ref<Record<string, string>>({})

function ensure() { void loadAgendor(token.value).then(() => requestAgendorLink(props.contactId)) }
onMounted(ensure)
watch(() => props.contactId, () => { ensure(); open.value = false; panelOpen.value = false; state.panel.value = null })

function place() {
  const r = btn.value?.getBoundingClientRect()
  if (!r) return
  const width = 260
  menuStyle.value = { top: `${r.bottom + 6}px`, left: `${Math.max(8, Math.min(window.innerWidth - width - 8, r.right - width))}px`, width: `${width}px` }
}

function toggle() {
  open.value = !open.value
  sub.value = null
  if (!open.value) return
  place()
  void state.load()
  void nextTick(() => menu.value?.querySelector<HTMLElement>('[role="menuitem"]')?.focus())
}

function toggleSub(name: 'move' | 'won' | 'pass') { sub.value = sub.value === name ? null : name }

function openPanel(section: AgendorPanelFocus) {
  open.value = false
  focus.value = section
  panelOpen.value = true
  if (!panel.value) void state.load()
}

function stagesOf(funnelId: string | null): AgendorStage[] {
  return panel.value?.funnels.find(f => f.id === funnelId)?.stages ?? []
}

async function move(d: AgendorDeal, stage: number) {
  if (await state.run(() => moveAgendorDeal(token.value, props.contactId!, props.conversationId, d.id, d.funnelId!, stage))) open.value = false
}

async function win(d: AgendorDeal) {
  open.value = false
  const ok = await Swal.fire({ title: 'Marcar como ganho?', text: `"${d.title}" vira ganho no Agendor e a conversa ganha a etiqueta de cliente.`, icon: 'question',
    showCancelButton: true, confirmButtonText: 'Marcar ganho', cancelButtonText: 'Cancelar' })
  if (ok.isConfirmed) await state.run(() => setAgendorDealStatus(token.value, props.contactId!, props.conversationId, d.id, 'won'))
}

async function assume() {
  if (await state.run(() => setAgendorOwner(token.value, props.contactId!, props.conversationId, null))) open.value = false
}

async function pass(userId: string) {
  const target = panel.value?.users.find(u => u.id === userId)
  if (!(await state.run(() => setAgendorOwner(token.value, props.contactId!, props.conversationId, userId)))) return
  open.value = false
  if (target?.agentId && props.conversationId && target.agentId.toLowerCase() !== (props.conversationAgentId ?? '').toLowerCase()) {
    const ask = await Swal.fire({ title: `Transferir a conversa para ${target.name} também?`, text: 'O lead já é dele no Agendor.', icon: 'question',
      showCancelButton: true, confirmButtonText: 'Transferir', cancelButtonText: 'Só o lead' })
    if (ask.isConfirmed && !(await assignConversation(token.value, props.conversationId, target.agentId))) {
      state.problem.value = `Não deu pra transferir a conversa para ${target.name}.`
    }
  }
}

async function refresh() {
  await findAgendorContact(token.value, props.contactId!, props.conversationId)
  await state.load(true)
}

function onOutside(e: MouseEvent) {
  if (open.value && !menu.value?.contains(e.target as Node) && !btn.value?.contains(e.target as Node)) open.value = false
}
onMounted(() => { document.addEventListener('click', onOutside); window.addEventListener('scroll', place, true) })
onBeforeUnmount(() => { document.removeEventListener('click', onOutside); window.removeEventListener('scroll', place, true) })

return (_ctx: any,_cache: any) => {
  return (visible.value)
    ? (_openBlock(), _createElementBlock("span", {
        key: 0,
        class: _normalizeClass(["agm", [`agm--${__props.variant}`, { 'agm--on': !!link.value }]])
      }, [
        _createElementVNode("button", {
          ref_key: "btn",
          ref: btn,
          type: "button",
          class: "agm-btn",
          title: title.value,
          "aria-expanded": open.value,
          "aria-haspopup": "menu",
          onClick: _withModifiers(toggle, ["stop"])
        }, [...(_cache[14] || (_cache[14] = [
          _createStaticVNode("<svg width=\"14\" height=\"14\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.2\" stroke-linecap=\"round\" stroke-linejoin=\"round\" aria-hidden=\"true\" data-v-051bd136><rect x=\"3\" y=\"7\" width=\"18\" height=\"13\" rx=\"2\" data-v-051bd136></rect><path d=\"M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2\" data-v-051bd136></path><path d=\"M3 13h18\" data-v-051bd136></path></svg><span data-v-051bd136>Agendor</span><svg width=\"10\" height=\"10\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"3\" aria-hidden=\"true\" data-v-051bd136><polyline points=\"6 9 12 15 18 9\" data-v-051bd136></polyline></svg>", 3)
        ]))], 8, _hoisted_1),
        (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
          (open.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                ref_key: "menu",
                ref: menu,
                class: "agm-menu",
                role: "menu",
                style: _normalizeStyle(menuStyle.value),
                onClick: _cache[11] || (_cache[11] = _withModifiers(() => {}, ["stop"])),
                onKeydown: _cache[12] || (_cache[12] = _withKeys(($event: any) => (open.value = false), ["esc"]))
              }, [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("b", null, _toDisplayString(link.value?.personName ?? 'Não vinculado'), 1),
                  _createElementVNode("small", null, _toDisplayString(link.value ? [link.value.organizationName, link.value.ownerName ? `resp. ${link.value.ownerName}` : null].filter(Boolean).join(' · ') || 'Agendor' : 'Contato ainda não está no Agendor'), 1)
                ]),
                (!link.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("button", {
                        type: "button",
                        role: "menuitem",
                        class: "agm-item",
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (openPanel('send')))
                      }, "Enviar para o Agendor"),
                      _createElementVNode("button", {
                        type: "button",
                        role: "menuitem",
                        class: "agm-item",
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (openPanel('send')))
                      }, "Procurar pelo telefone")
                    ], 64))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createElementVNode("button", {
                        type: "button",
                        role: "menuitem",
                        class: "agm-item",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (openPanel(null)))
                      }, "Abrir painel do Agendor"),
                      (link.value.webUrl)
                        ? (_openBlock(), _createElementBlock("a", {
                            key: 0,
                            role: "menuitem",
                            class: "agm-item",
                            href: link.value.webUrl,
                            target: "_blank",
                            rel: "noopener",
                            onClick: _cache[3] || (_cache[3] = ($event: any) => (open.value = false))
                          }, "Ver no Agendor ↗", 8, _hoisted_3))
                        : _createCommentVNode("", true),
                      _cache[18] || (_cache[18] = _createElementVNode("div", { class: "agm-sep" }, "Negócio", -1)),
                      _createElementVNode("button", {
                        type: "button",
                        role: "menuitem",
                        class: "agm-item",
                        onClick: _cache[4] || (_cache[4] = ($event: any) => (openPanel('deal')))
                      }, "Criar negócio"),
                      (canMove.value)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 1,
                            type: "button",
                            role: "menuitem",
                            class: "agm-item agm-sub",
                            "aria-expanded": sub.value === 'move',
                            disabled: !ongoing.value.length,
                            onClick: _cache[5] || (_cache[5] = ($event: any) => (toggleSub('move')))
                          }, [
                            _cache[15] || (_cache[15] = _createTextVNode(" Mover etapa ", -1)),
                            _createElementVNode("span", null, _toDisplayString(sub.value === 'move' ? '▾' : '▸'), 1)
                          ], 8, _hoisted_4))
                        : _createCommentVNode("", true),
                      (sub.value === 'move')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(ongoing.value, (d) => {
                              return (_openBlock(), _createElementBlock(_Fragment, {
                                key: d.id
                              }, [
                                _createElementVNode("div", _hoisted_6, _toDisplayString(d.title), 1),
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(stagesOf(d.funnelId), (s) => {
                                  return (_openBlock(), _createElementBlock("button", {
                                    key: s.id,
                                    type: "button",
                                    role: "menuitem",
                                    class: "agm-item agm-item--nested",
                                    disabled: busy.value || d.stageSequence === s.sequence,
                                    onClick: ($event: any) => (move(d, s.sequence))
                                  }, _toDisplayString(d.stageSequence === s.sequence ? '● ' : '') + _toDisplayString(s.name), 9, _hoisted_7))
                                }), 128))
                              ], 64))
                            }), 128))
                          ]))
                        : _createCommentVNode("", true),
                      (canWinLose.value)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                            _createElementVNode("button", {
                              type: "button",
                              role: "menuitem",
                              class: "agm-item agm-sub",
                              "aria-expanded": sub.value === 'won',
                              disabled: !ongoing.value.length,
                              onClick: _cache[6] || (_cache[6] = ($event: any) => (toggleSub('won')))
                            }, [
                              _cache[16] || (_cache[16] = _createTextVNode(" Marcar ganho ", -1)),
                              _createElementVNode("span", null, _toDisplayString(sub.value === 'won' ? '▾' : '▸'), 1)
                            ], 8, _hoisted_8),
                            (sub.value === 'won')
                              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(ongoing.value, (d) => {
                                    return (_openBlock(), _createElementBlock("button", {
                                      key: d.id,
                                      type: "button",
                                      role: "menuitem",
                                      class: "agm-item agm-item--nested",
                                      disabled: busy.value,
                                      onClick: ($event: any) => (win(d))
                                    }, _toDisplayString(d.title), 9, _hoisted_10))
                                  }), 128))
                                ]))
                              : _createCommentVNode("", true),
                            _createElementVNode("button", {
                              type: "button",
                              role: "menuitem",
                              class: "agm-item",
                              disabled: !ongoing.value.length,
                              onClick: _cache[7] || (_cache[7] = ($event: any) => (openPanel('deal')))
                            }, "Marcar perdido…", 8, _hoisted_11)
                          ], 64))
                        : _createCommentVNode("", true),
                      (_unref(state).loading.value && !panel.value)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_12, "Carregando negócios…"))
                        : _createCommentVNode("", true),
                      _cache[19] || (_cache[19] = _createElementVNode("div", { class: "agm-sep" }, "Seguir o lead", -1)),
                      _createElementVNode("button", {
                        type: "button",
                        role: "menuitem",
                        class: "agm-item",
                        onClick: _cache[8] || (_cache[8] = ($event: any) => (openPanel('task')))
                      }, "Criar retorno"),
                      _createElementVNode("button", {
                        type: "button",
                        role: "menuitem",
                        class: "agm-item",
                        onClick: _cache[9] || (_cache[9] = ($event: any) => (openPanel('note')))
                      }, "Registrar anotação"),
                      (panel.value?.myUserId && !ownerIsMe.value)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 5,
                            type: "button",
                            role: "menuitem",
                            class: "agm-item",
                            disabled: busy.value,
                            onClick: assume
                          }, "Assumir lead", 8, _hoisted_13))
                        : _createCommentVNode("", true),
                      _createElementVNode("button", {
                        type: "button",
                        role: "menuitem",
                        class: "agm-item agm-sub",
                        "aria-expanded": sub.value === 'pass',
                        disabled: !passTargets.value.length,
                        onClick: _cache[10] || (_cache[10] = ($event: any) => (toggleSub('pass')))
                      }, [
                        _cache[17] || (_cache[17] = _createTextVNode(" Passar lead para ", -1)),
                        _createElementVNode("span", null, _toDisplayString(sub.value === 'pass' ? '▾' : '▸'), 1)
                      ], 8, _hoisted_14),
                      (sub.value === 'pass')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(passTargets.value, (u) => {
                              return (_openBlock(), _createElementBlock("button", {
                                key: u.id,
                                type: "button",
                                role: "menuitem",
                                class: "agm-item agm-item--nested",
                                disabled: busy.value,
                                onClick: ($event: any) => (pass(u.id))
                              }, _toDisplayString(u.name), 9, _hoisted_16))
                            }), 128))
                          ]))
                        : _createCommentVNode("", true),
                      _cache[20] || (_cache[20] = _createElementVNode("div", { class: "agm-sep" }, null, -1)),
                      _createElementVNode("button", {
                        type: "button",
                        role: "menuitem",
                        class: "agm-item",
                        disabled: busy.value || _unref(state).loading.value,
                        onClick: refresh
                      }, "Atualizar", 8, _hoisted_17)
                    ], 64)),
                (_unref(state).problem.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_18, _toDisplayString(_unref(state).problem.value), 1))
                  : _createCommentVNode("", true)
              ], 36))
            : _createCommentVNode("", true)
        ])),
        (panelOpen.value && __props.contactId)
          ? (_openBlock(), _createBlock(AgendorPanel, {
              key: 0,
              state: _unref(state),
              "contact-id": __props.contactId,
              "conversation-id": __props.conversationId,
              "conversation-agent-id": __props.conversationAgentId,
              "contact-name": __props.contactName,
              focus: focus.value,
              onClose: _cache[13] || (_cache[13] = ($event: any) => (panelOpen.value = false))
            }, null, 8, ["state", "contact-id", "conversation-id", "conversation-agent-id", "contact-name", "focus"]))
          : _createCommentVNode("", true)
      ], 2))
    : _createCommentVNode("", true)
}
}

})